import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /**
   * @type {{ request: import('@shopify/events').CartLinesUpdateEvent, sourceModal: HTMLDialogElement | null,
   * confirmed: boolean, rendered: boolean, onClose?: () => void } | undefined}
   */
  #pendingAutoOpen;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.addEventListener('cart:rendered', this.#handleCartRendered);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.removeEventListener('cart:rendered', this.#handleCartRendered);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#clearPendingAutoOpen();
  }

  /** @param {CustomEvent} event */
  #handleCartRendered = (event) => {
    const pending = this.#pendingAutoOpen;
    if (pending && event.detail?.request === pending.request) {
      if (event.detail.didError) {
        this.#clearPendingAutoOpen();
      } else {
        pending.rendered = true;
        this.#tryAutoOpen();
      }
    }

    // Open drawers still need their summary measured after every native morph.
    if (this.#themeDrawer?.hasAttribute('open')) {
      requestAnimationFrame(() => this.#updateStickyState());
    }
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // Modal quick-add must finish restoring focus before opening the cart.
    // Non-modal previews remain interactive and do not require this handoff.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    this.#clearPendingAutoOpen();
    if (shouldAutoOpen && event.promise) {
      this.#pendingAutoOpen = { request: event, sourceModal, confirmed: false, rendered: false };
    }

    event.promise
      ?.then(({ detail }) => {
        const pending = this.#pendingAutoOpen;
        if (pending?.request === event) {
          if (detail?.didError) {
            this.#clearPendingAutoOpen();
          } else {
            pending.confirmed = true;
            this.#tryAutoOpen();
          }
        }
        requestAnimationFrame(() => this.#updateStickyState());
      })
      .catch((error) => {
        if (this.#pendingAutoOpen?.request === event) this.#clearPendingAutoOpen();
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #tryAutoOpen() {
    const pending = this.#pendingAutoOpen;
    if (!pending?.confirmed || !pending.rendered) return;
    if (!this.isConnected || !this.hasAttribute('auto-open') || this.#isCartEmpty()) {
      this.#clearPendingAutoOpen();
      return;
    }

    if (pending.sourceModal?.open) {
      if (!pending.onClose) {
        pending.onClose = () => this.#tryAutoOpen();
        pending.sourceModal.addEventListener('close', pending.onClose, { once: true });
      }
      return;
    }

    this.#clearPendingAutoOpen();
    if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
    requestAnimationFrame(() => this.#updateStickyState());
  }

  #clearPendingAutoOpen() {
    const pending = this.#pendingAutoOpen;
    if (pending?.onClose) pending.sourceModal?.removeEventListener('close', pending.onClose);
    this.#pendingAutoOpen = undefined;
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
